
import { mergeMap, map, filter, take } from 'rxjs/operators'
import { Epic } from 'interfaces/middleware'
import { combineEpics } from 'redux-observable'
import { processTUCIError } from 'helpers/ajaxHelpers'
import { get, post } from 'helpers/observableAjaxHelpers'
import { normalize } from 'helpers/normalizeHelpers'
import { Action } from 'actions'
import {
  CONFIRM_MEMBER_CREDIT_URL,
  GET_CREDIT_ACCOUNTS_URL,
  GET_CREDIT_TOTALS_URL,
  SET_MEMBER_CREDIT_EXTENDED_URL,
  SET_MEMBER_CREDIT_URL,
} from 'interfaces/ajax'
import { resolveCategory } from 'helpers/categoriesHelpers'
import { getTimeframeLastMonth } from 'helpers/dateTimeHelpers'
import {
  creditLinkedActionCreator,
  GET_CREDIT_ACCOUNTS,
  GET_CREDIT_TOTALS,
  SEND_CREDIT_ANSWERS,
  setCreditAccountsActionCreator,
  setCreditErrorActionCreator,
  setCreditQuestionsActionCreator,
  setCreditTotalsActionCreator,
} from 'actions/creditActions'
import { CreditAccount } from 'interfaces/credit'
import { captureWarning, UNEXPECTED_ENDPOINT_STATUS } from 'helpers/errorHelpers'
import { empty, from, of } from 'rxjs'
import { metaSelector } from 'selectors/globalSelectors'
import { CREDIT_USER_BLOCKED, TUCI_VERIFICATION_FAILED } from 'constants/errors'
import { FORM_SETTINGS_CREDIT, FORM_SETTINGS_CREDIT_EXTENDED } from 'constants/forms'
import { SET_MEMBER_INFO, setMemberInfoActionCreator } from 'actions/memberActions'
import { addAccomplishmentActionCreator } from 'actions/bountyActions'
import { byType } from 'rxOperators/byType'
import { distinctByField } from 'rxOperators/distinctByField'
import { mapSafe, mergeMapSafe } from 'rxOperators/mergeMapSafe'
import { catchSafe } from 'rxOperators/catchSafe'
import { formSubmitted } from 'rxOperators/formsLegacy'

// ===========================================================================//
//                                  LINKAGE                                   //
// ===========================================================================//

const creditFormEpic: Epic = (action$) =>
  action$
    .pipe(formSubmitted(
      FORM_SETTINGS_CREDIT, FORM_SETTINGS_CREDIT_EXTENDED,
    )).pipe(
      mergeMap(({ payload }) => {
        if (
          payload.formName !== FORM_SETTINGS_CREDIT
        && payload.formName !== FORM_SETTINGS_CREDIT_EXTENDED
        ) {
          return empty()
        }

        const { response } = payload
        const actions: Action[] = []
        const phase = payload.formName === FORM_SETTINGS_CREDIT
          ? 'form-simple'
          : 'form-extended'

        if (response.status === 'verification_required') {
          actions.push(setCreditQuestionsActionCreator(response.questions))

        } else if (response.status === 'ineligible') {
          const { error_code } = response
          actions.push(setCreditErrorActionCreator(error_code, phase))

        } else if (response.status === 'blocked') {
          actions.push(setCreditErrorActionCreator(CREDIT_USER_BLOCKED, phase))

        } else {
          const endpointUrl = phase === 'form-simple'
            ? SET_MEMBER_CREDIT_URL
            : SET_MEMBER_CREDIT_EXTENDED_URL

          captureWarning(UNEXPECTED_ENDPOINT_STATUS, {
            endpointUrl,
            status: (response as any).status,
          })

          actions.push(setCreditErrorActionCreator(TUCI_VERIFICATION_FAILED, phase))
        }

        return from(actions)
      }))


const confirmCreditFormEpic: Epic = (action$, store) =>
  action$
    .pipe(byType(SEND_CREDIT_ANSWERS)).pipe(
      mergeMap(() => {
        const state = store.getState()
        const body = {
          answers: state.app.creditscore.linkage.answers,
        }

        return post(CONFIRM_MEMBER_CREDIT_URL, { body }).pipe(
          mergeMap(({ response, response: { member }}) => {
            const actions: Action[] = [setMemberInfoActionCreator(member)]

            // if we check only status here typescript will not understand
            // that we have additional fields when status === 'in_progress'
            switch (response.status) {
              case 'success': {
                if (response.accomplishment) {
                  actions.push(addAccomplishmentActionCreator(response.accomplishment))
                }
                break
              }

              case 'in_progress':
                const { questions } = response
                actions.push(setCreditQuestionsActionCreator(questions))
                break

              case 'failed':
                actions.push(setCreditErrorActionCreator(TUCI_VERIFICATION_FAILED, 'questions'))
                break

              case 'ineligible':
                const { error_code } = response
                actions.push(setCreditErrorActionCreator(error_code, 'questions'))
                break

              default:
                captureWarning(UNEXPECTED_ENDPOINT_STATUS, {
                  endpointUrl: CONFIRM_MEMBER_CREDIT_URL,
                  status: (response as any).status,
                })

                actions.push(setCreditErrorActionCreator(TUCI_VERIFICATION_FAILED, 'questions'))
            }
            return from(actions)
          }))
        // tslint:disable-next-line:no-unnecessary-callback-wrapper
          .pipe(catchSafe((error) => processTUCIError(error)))
      }))

const creditLinkedEpic: Epic = (action$) =>
  action$
    .pipe(byType(SET_MEMBER_INFO)).pipe(
      filter((a) => a.payload.is_creditfile_linked === false),
      take(1),
      mergeMap(() =>
        action$
          .pipe(byType(SET_MEMBER_INFO)).pipe(
            filter((a) => a.payload.is_creditfile_linked === true),
            take(1),
            map(creditLinkedActionCreator))
      ))

// ===========================================================================//
//                                  DATA                                      //
// ===========================================================================//

const creditAccountsEpic: Epic = (action$, store) =>
  action$
    .pipe(byType(GET_CREDIT_ACCOUNTS))
    .pipe(distinctByField('payload')).pipe(
      mergeMap(({ payload: timeframe }) =>
        get(GET_CREDIT_ACCOUNTS_URL, { date: getTimeframeLastMonth(timeframe) })
          .pipe(mergeMapSafe(({ response }) => {
            const meta = metaSelector(store.getState())
            const categories = meta.debt_categories
            const accounts: CreditAccount[] = response.map((account) => (
              { ...account, category: categories.byKey[resolveCategory(account.category, false)] }
            ))

            return of(setCreditAccountsActionCreator(timeframe, normalize(accounts, 'id')))
          }))
      ))

const creditTotalsEpic: Epic = (action$) =>
  action$
    .pipe(byType(GET_CREDIT_TOTALS))
    .pipe(distinctByField('payload')).pipe(
      mergeMap(({ payload: timeframe }) =>
        get(GET_CREDIT_TOTALS_URL, { date: getTimeframeLastMonth(timeframe) })
          .pipe(mapSafe(({ response }) => setCreditTotalsActionCreator(timeframe, response)))
      ))

export default combineEpics(
  creditFormEpic,
  confirmCreditFormEpic,
  creditLinkedEpic,

  creditAccountsEpic,
  creditTotalsEpic,
)
